import { user } from "@/api/userSetController3311";
import Footer from "@/components/Footer";
import RightContent from "@/components/RightContent";
import {
  API_PREFIX_PHP_ERP,
  API_PREFIX_PHP_OS,
  EnvMap,
  ERROR_CODE,
  SUCCESS_CODE_LIST,
} from "@/constant";

import { navLogin } from "@/utils/nav";
import type { ShopData } from "@/utils/storage";
import {
  getCurrentShopData,
  getLocalEnvStorage,
  getToken,
  setCurrentShop,
  setCurrentUser,
} from "@/utils/storage";
import { sendLog } from "@/utils/tracker";
import { LinkOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import * as Sentry from "@sentry/browser";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type {
  RequestConfig,
  RunTimeLayoutConfig,
  RuntimeConfig,
} from "@umijs/max";
import { Access, history, Link } from "@umijs/max";
import { Space, Divider, App } from "antd";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { AxiosError } from "axios";
import qs from "qs";
import defaultSettings from "../config/defaultSettings";
import styles from "./app.less";
import { PAGES, TRACKER_ROUTE_NAME } from "@/constant/pages";
import TrackerWrapper from "@/components/Tracker";
import {
  postUserRolesTemplateButton,
  PostUserRolesTemplateButtonRequest,
} from "./openapi/baob/pc.Role";
import { PermissionKeys, TSupplyConfig } from "./types";
import EscapeAntd, { message } from "./components/EscapeAntd";
import { GetUserResponse } from "@/openapi/manage";
import AuthModal from "@/components/Modal/AuthModal";
import FloatFooter from "@/components/Footer/Float";
import GlobalModals from "@/components/Modal/GlobalModals";
import YizhePrintEventHandler from "@/components/YizhePrintEventHandler";
import { customMenuItemRenderer } from "@/utils/menuItemRenderer";
import { postBaobSettingPcSupplyConfig } from "./openapi/baob/SettingPc/api.client.axios";

// dev or test
const isDevOrTest = REACT_APP_ENV === "dev" || REACT_APP_ENV === "test";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
  },
});
let environment;
// 环境名和其他项目保持统一
if (REACT_APP_ENV === "test") {
  environment = "qa";
} else if (REACT_APP_ENV === "prod") {
  environment = "production";
} else if (REACT_APP_ENV === "pre") {
  environment = "staging";
}

// Only initialize Sentry in production environment
if (REACT_APP_ENV === "prod") {
  Sentry.init({
    dsn: "https://d3e754536c41476a8eaad321ebe17ebe@glitchtip.yishouapp.com/2",
    integrations: [Sentry.replayIntegration()],
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment,
  });
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: GetUserResponse;
  loading?: boolean;
  fetchUserInfo?: () => Promise<GetUserResponse | undefined>;
  currentShop?: ShopData;
  permissionKeys?: PermissionKeys;
  fetchPermissionKeys?: (
    params: PostUserRolesTemplateButtonRequest,
  ) => Promise<PermissionKeys | undefined>;
  fetchSupplyConfig?: () => Promise<TSupplyConfig | undefined>;
  supplyConfig?: TSupplyConfig;
}> {
  const fetchUserInfo = async () => {
    try {
      return await user();
    } catch (error) {
      navLogin();
    }
    return undefined;
  };
  const fetchPermissionKeys = async (
    params: PostUserRolesTemplateButtonRequest,
  ) => {
    try {
      return await postUserRolesTemplateButton(params).then(
        (res) => res?.keys as PermissionKeys | undefined,
      );
    } catch (error) {
      navLogin();
    }
    return undefined;
  };
  const fetchSupplyConfig = async () => {
    try {
      const res = await postBaobSettingPcSupplyConfig({});
      return res?.data;
    } catch (error) {
      navLogin();
    }
    return undefined;
  };

  // 如果有登录数据，执行
  if (getToken()) {
    const currentUser = await fetchUserInfo();

    setCurrentUser(currentUser);
    Sentry.setUser({
      id: currentUser?.userId,
      username: currentUser?.userName,
    });
    const currentShop = setCurrentShop(currentUser);
    const permissionKeys = await fetchPermissionKeys({
      shop_id: currentShop.shopId?.toString(),
      user_id: currentUser?.userId?.toString(),
    });
    const supplyConfig = await fetchSupplyConfig();
    return {
      fetchUserInfo,
      currentUser,
      currentShop,
      fetchPermissionKeys,
      permissionKeys: permissionKeys,
      settings: defaultSettings,
      fetchSupplyConfig,
      supplyConfig,
    };
  }
  return {
    fetchUserInfo,
    fetchPermissionKeys,
    settings: defaultSettings,
  };
}
// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    title: "爆版商家后台",
    logo: "https://static3.yishouapp.com/obs/tools/baoban/logo.png",
    rightContentRender: () => (
      <App>
        <QueryClientProvider client={queryClient}>
          <RightContent />
        </QueryClientProvider>
      </App>
    ),
    disableContentMargin: false,
    // waterMarkProps: {
    //   content: initialState?.currentUser?.userName,
    // },
    headerTitleRender: (logo, title) => {
      return (
        <Space className={styles.title}>
          {logo}
          {title}
        </Space>
      );
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !getToken() &&
        ![PAGES.USER_LOGIN, PAGES.CONNECT].includes(location.pathname as PAGES)
      ) {
        history.push(PAGES.USER_LOGIN);
      }
    },
    links: isDevOrTest
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,

    // 自定义菜单项渲染
    menuItemRender: customMenuItemRenderer,
    token: {
      sider: {
        // UI 要求使用默认
        colorTextMenuSelected: "#FCAB3D",
        colorBgMenuItemSelected: "none",
        // colorTextMenuItemHover: '#FCAB3D',
      },
      pageContainer: {
        paddingBlockPageContainerContent: 16,
        paddingInlinePageContainerContent: 24,
      },
    },
    siderWidth: 208,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <App>
          <TrackerWrapper>
            <QueryClientProvider client={queryClient}>
              {/* 衣者打印注册事件*/}
              <YizhePrintEventHandler />
              <EscapeAntd />
              {children}
              {/*{!props.location?.pathname?.includes('/login') && (*/}
              {/*  <SettingDrawer*/}
              {/*    disableUrlParams*/}
              {/*    enableDarkTheme*/}
              {/*    settings={initialState?.settings}*/}
              {/*    onSettingChange={(settings) => {*/}
              {/*      setInitialState((preInitialState) => ({*/}
              {/*        ...preInitialState,*/}
              {/*        settings,*/}
              {/*      }));*/}
              {/*    }}*/}
              {/*  />*/}
              {/*)}*/}
              <AuthModal />
              <FloatFooter />
              <GlobalModals />
            </QueryClientProvider>
          </TrackerWrapper>
        </App>
      );
    },
    breadcrumbProps: {
      itemRender: (route, params, routes) => {
        if (
          route.linkPath === routes[0].linkPath ||
          routes.length === 2 ||
          route.linkPath === routes[routes.length - 1].linkPath
        ) {
          return <span>{route.breadcrumbName}</span>;
        }
        return route.linkPath ? (
          <Link to={route.linkPath}>{route.breadcrumbName}</Link>
        ) : null;
      },
      className: "pb-4",
    },
    headerContentRender: () => {
      return (
        <Space>
          <Divider type="vertical" />
          <Access
            accessible={
              initialState?.permissionKeys?.Header_DataCenter ?? false
            }
          >
            <Link className={styles.link} to="/data/overview">
              <span
                data-tracker={JSON.stringify({
                  topic: "pcMainFloatingFrame",
                  activity: "主悬浮框",
                  data: {
                    click_source: "数据中心",
                  },
                })}
              >
                数据中心
              </span>
            </Link>
          </Access>
          <Access
            accessible={
              initialState?.permissionKeys?.Header_LearningCenter ?? false
            }
          >
            <a
              className={styles.link}
              href="https://yb7ao262ru.feishu.cn/wiki/VxUAwsvYXimvbPkAnXhcAZKFnDa"
              target="_blank"
              rel="noreferrer"
            >
              <span
                data-tracker={JSON.stringify({
                  topic: "pcMainFloatingFrame",
                  activity: "主悬浮框",
                  data: {
                    click_source: "学习中心",
                  },
                })}
              >
                学习中心
              </span>
            </a>
          </Access>
        </Space>
      );
    },

    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: API_URL,
  requestInterceptors: [
    (config: AxiosRequestConfig) => {
      const currentShop = getCurrentShopData();
      /**
       * 添加请求头公共参数
       * supplier：等于 token
       * shopId：当前选择的店铺 id
       * LOCAL_ENV: 联调环境
       */
      config.headers = {
        ...config.headers,
        supplier: getToken() || "",
        shopId: currentShop.shopId || "",
      };
      if (LOCAL_ENV) {
        if (config.url?.includes(`${API_PREFIX_PHP_ERP}/`)) {
          const {
            erp,
            erpHost = "",
            erpSuffix = EnvMap.erp.suffix[0],
          } = getLocalEnvStorage();
          config.headers["LOCAL-ENV"] = `${erpHost}${erp || ""}${erpSuffix}`;
        } else if (config.url?.includes(`${API_PREFIX_PHP_OS}/`)) {
          const {
            os,
            osHost = "",
            osSuffix = EnvMap.os.suffix[0],
          } = getLocalEnvStorage();

          config.headers["LOCAL-ENV"] = `${osHost}${os || ""}${osSuffix}`;
        }
      }
      // console.log('---config', config);
      return config;
    },
  ],
  responseInterceptors: [
    [
      (response: AxiosResponse) => {
        // console.log('----response', response);
        if (
          Object.prototype.toString.call(response.data).slice(8, -1) ===
          "Object"
        ) {
          const { code, data = {}, msg } = response.data;
          const success = SUCCESS_CODE_LIST.includes(code);
          if (code === ERROR_CODE.notLoggedIn) {
            // 未登录
            navLogin();
          } else if (!success && response.config.showMessage) {
            // 错误提示
            message.error(msg);
          }
          if (success) {
            // 如果返回值是对象，则解构后使用，否则直接使用
            if (Array.isArray(data)) {
              response.data = data;
            } else {
              response.data = { success, code, msg, ...(data || {}) };
            }
          } else {
            // 业务错误抛出
            response.data = Promise.reject(response.data);
          }
          return response;
        } else {
          return response;
        }
      },
    ],
  ],
  timeout: 120000,
  errorConfig: {
    errorHandler: (error, opts) => {
      console.log("----errorHandler", error, opts);
      if (error instanceof AxiosError) {
        message.error("系统错误，请稍后再试");
      }
    },
    errorThrower: (e: AxiosError) => {
      console.log("---errorThrower res", e);
      // message.error(res.msg);
    },
  },
};

/**
 *  在初始加载和路由切换时做一些事情--比如用于做埋点统计/比如用于设置标题
 **/
export const onRouteChange: RuntimeConfig["onRouteChange"] = ({
  // matchedRoutes,
  location,
  // routes,
}) => {
  // 首页和登录页不上报
  if (![PAGES.HOME, PAGES.USER_LOGIN].includes(location.pathname)) {
    // 区分商品发布与商品编辑
    if (location.pathname === TRACKER_ROUTE_NAME[PAGES.GOODS_DETAIL]) {
      const query = qs.parse(location.search.replace("?", ""));
      sendLog("baobanPCExposure", "爆版PC端曝光", {
        page_name: query.goods_no
          ? "商品编辑"
          : TRACKER_ROUTE_NAME[
              location.pathname as keyof typeof TRACKER_ROUTE_NAME
            ],
      });
      return;
    }
    sendLog("baobanPCExposure", "爆版PC端曝光", {
      page_name:
        TRACKER_ROUTE_NAME[
          location.pathname as keyof typeof TRACKER_ROUTE_NAME
        ],
    });
  }
};
